import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { DropdownFilter } from "../dropdown-filter";
import { SearchInput } from "../search-input";
import { useStyles as useSelectableTableStyles } from "../selectable-table/style";
import { useStyles as useAccountManagementStyles } from "../../pages/account-management/style";
import { useGetProfilesByRole } from "../../service/api";
import { Auth0Profile } from "../../types";
import { highlightText } from "../../utils/highlight-text";

const ROLE_FILTER_OPTIONS = ["All", "Trainer", "Trainee"];

export const ApamoveAccountsTable = () => {
  const { t } = useTranslation();
  const tableClasses = useSelectableTableStyles();
  const pageClasses = useAccountManagementStyles();
  const { data: trainers = [], isLoading: loadingTrainers, error: trainersError } = useGetProfilesByRole("trainer");
  const { data: trainees = [], isLoading: loadingTrainees, error: traineesError } = useGetProfilesByRole("trainee");

  const [roleFilter, setRoleFilter] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(8);

  const isLoading = loadingTrainers || loadingTrainees;
  const error = trainersError || traineesError;

  const profiles = useMemo(() => [...trainers, ...trainees], [trainers, trainees]);

  const filteredProfiles = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return profiles.filter((profile: Auth0Profile) => {
      const matchesRole = roleFilter === "All" || profile.role === roleFilter.toLowerCase();
      const matchesQuery =
        !query ||
        profile.full_name?.toLowerCase().includes(query) ||
        profile.email?.toLowerCase().includes(query);
      return matchesRole && matchesQuery;
    });
  }, [profiles, roleFilter, searchQuery]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <Box className={pageClasses.innerContainer}>
        <Typography className={pageClasses.accounts}>
          {t("accountManagement.apamoveTable.usersLabel")}{" "}
          <span className={pageClasses.accountsNum}>({filteredProfiles.length})</span>
        </Typography>
        <Box className={pageClasses.filters}>
          <DropdownFilter
            label={t("accountManagement.apamoveTable.roleFilter")}
            options={ROLE_FILTER_OPTIONS}
            selected={roleFilter}
            onSelect={setRoleFilter}
          />
          <SearchInput query={searchQuery} onSearch={setSearchQuery} />
        </Box>
      </Box>

      {isLoading || error ? (
        <Box className={pageClasses.loadingAndErrorWrapper}>
          {isLoading ? (
            <CircularProgress />
          ) : (
            <Typography color="error">{t("accountManagement.apamoveTable.errorLoading")}</Typography>
          )}
        </Box>
      ) : (
        <Paper className={tableClasses.tableContainer}>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell className={tableClasses.tableHead}>{t("accountManagement.apamoveTable.colFullName")}</TableCell>
                  <TableCell className={tableClasses.tableHead}>{t("accountManagement.apamoveTable.colEmail")}</TableCell>
                  <TableCell className={tableClasses.tableHead}>{t("accountManagement.apamoveTable.colRole")}</TableCell>
                  <TableCell className={tableClasses.tableHead}>{t("accountManagement.apamoveTable.colStatus")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProfiles.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                        {t("accountManagement.apamoveTable.empty")}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredProfiles
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((profile: Auth0Profile) => (
                      <TableRow key={profile.id}>
                        <TableCell>{profile.full_name ? highlightText(profile.full_name, searchQuery) : "—"}</TableCell>
                        <TableCell>{profile.email ? highlightText(profile.email, searchQuery) : "—"}</TableCell>
                        <TableCell>
                          <Chip
                            label={profile.role === "trainer" ? t("accountManagement.apamoveTable.roleTrainer") : t("accountManagement.apamoveTable.roleTrainee")}
                            color={profile.role === "trainer" ? "primary" : "default"}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{profile.status}</TableCell>
                      </TableRow>
                    ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[8, 25, 50]}
            component="div"
            count={filteredProfiles.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
    </Box>
  );
};
